import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { readProfileTableLayout, writeProfileTableLayout } from './layoutPersistence'
import { ProfileColumnDef } from './types'

const COLLAPSED_COLUMN_WIDTH = 36

interface UseProfileTableLayoutOptions<TMiddleKey extends string, TFixedKey extends string> {
    middleColumns: ProfileColumnDef<TMiddleKey>[]
    fixedColumns: ProfileColumnDef<TFixedKey>[]
    storageKey?: string
    initialHiddenColumns?: TMiddleKey[]
    deferDefaultLayoutPersistence?: boolean
}

export const useProfileTableLayout = <TMiddleKey extends string, TFixedKey extends string>({
    middleColumns,
    fixedColumns,
    storageKey,
    initialHiddenColumns,
    deferDefaultLayoutPersistence = false,
}: UseProfileTableLayoutOptions<TMiddleKey, TFixedKey>) => {
    type TAllKey = TFixedKey | TMiddleKey

    const columnDefinitionMap = useMemo(() => {
        const allColumns = [...fixedColumns, ...middleColumns]
        return allColumns.reduce(
            (acc, column) => {
                acc[column.key as TAllKey] = column
                return acc
            },
            {} as Record<TAllKey, ProfileColumnDef<TAllKey>>
        )
    }, [fixedColumns, middleColumns])

    const initialWidths = useMemo(() => {
        const allColumns = [...fixedColumns, ...middleColumns]
        return allColumns.reduce(
            (acc, column) => {
                acc[column.key as string] = column.defaultWidth
                return acc
            },
            {} as Record<string, number>
        )
    }, [fixedColumns, middleColumns])

    const persistedLayout = useMemo(() => {
        if (!storageKey) return null
        return readProfileTableLayout<TMiddleKey, TAllKey>(storageKey)
    }, [storageKey])
    const hadPersistedLayoutAtMount = useRef(persistedLayout !== null)
    const userChangedLayout = useRef(false)

    const normalizeMiddleColumnOrder = useCallback(
        (order: TMiddleKey[] | undefined): TMiddleKey[] => {
            const available = middleColumns.map((col) => col.key)
            const validOrder = (order ?? []).filter((col): col is TMiddleKey => available.includes(col))
            available.forEach((col) => {
                if (!validOrder.includes(col)) {
                    validOrder.push(col)
                }
            })
            return validOrder
        },
        [middleColumns]
    )

    const [middleColumnOrder, setMiddleColumnOrder] = useState<TMiddleKey[]>(() => normalizeMiddleColumnOrder(persistedLayout?.middleColumnOrder))
    const [hiddenColumns, setHiddenColumns] = useState<TMiddleKey[]>(() => persistedLayout?.hiddenColumns ?? initialHiddenColumns ?? [])
    const [collapsedColumns, setCollapsedColumns] = useState<TMiddleKey[]>(() => persistedLayout?.collapsedColumns ?? [])
    const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => ({
        ...initialWidths,
        ...(persistedLayout?.columnWidths ?? {}),
    }))

    const [activeColumn, setActiveColumn] = useState<TMiddleKey | null>(null)
    const [overColumn, setOverColumn] = useState<TMiddleKey | null>(null)

    useEffect(() => {
        if (!deferDefaultLayoutPersistence || hadPersistedLayoutAtMount.current || userChangedLayout.current) return
        const defaults = initialHiddenColumns ?? []
        setHiddenColumns((current) => (current.length === defaults.length && current.every((column, index) => column === defaults[index]) ? current : defaults))
    }, [deferDefaultLayoutPersistence, initialHiddenColumns])

    // Normalize on column definition changes
    useEffect(() => {
        setMiddleColumnOrder((current) => {
            const next = normalizeMiddleColumnOrder(current)
            return current.join('|') === next.join('|') ? current : next
        })
        setHiddenColumns((current) => current.filter((col) => !!columnDefinitionMap[col as TAllKey]))
        setCollapsedColumns((current) => current.filter((col) => !!columnDefinitionMap[col as TAllKey]))
        setColumnWidths((current) => {
            const merged: Record<string, number> = { ...initialWidths, ...current }
            const next: Record<string, number> = { ...merged }
            for (const [key, width] of Object.entries(merged)) {
                const definition = columnDefinitionMap[key as TAllKey]
                if (!definition) continue
                const minWidth = definition.minWidth
                const maxWidth = definition.maxWidth ?? Number.POSITIVE_INFINITY
                next[key] = Math.max(minWidth, Math.min(maxWidth, width))
            }
            return next
        })
    }, [columnDefinitionMap, initialWidths, normalizeMiddleColumnOrder])

    // Persist layout
    useEffect(() => {
        if (!storageKey) return
        if (deferDefaultLayoutPersistence && !hadPersistedLayoutAtMount.current && !userChangedLayout.current) return
        writeProfileTableLayout<TMiddleKey, TAllKey>(storageKey, {
            middleColumnOrder,
            hiddenColumns,
            collapsedColumns,
            columnWidths: columnWidths as Partial<Record<TAllKey, number>>,
        })
    }, [collapsedColumns, columnWidths, deferDefaultLayoutPersistence, hiddenColumns, middleColumnOrder, storageKey])

    const visibleMiddleColumns = useMemo(
        () =>
            middleColumnOrder
                .filter((col) => !hiddenColumns.includes(col))
                .map((col) => columnDefinitionMap[col as TAllKey])
                .filter(Boolean),
        [columnDefinitionMap, hiddenColumns, middleColumnOrder]
    )

    const visibleColumns = useMemo(() => {
        if (fixedColumns.length === 0) return visibleMiddleColumns.filter(Boolean)
        if (fixedColumns.length === 1) return [fixedColumns[0], ...visibleMiddleColumns].filter(Boolean)
        // Every fixed column but the last leads the row (a table may pin several on the left); the last one trails it.
        return [...fixedColumns.slice(0, -1), ...visibleMiddleColumns, fixedColumns[fixedColumns.length - 1]].filter(Boolean)
    }, [fixedColumns, visibleMiddleColumns])

    const addableColumns = useMemo(
        () => hiddenColumns.map((col) => ({ key: col, label: columnDefinitionMap[col as TAllKey]?.label || col })),
        [columnDefinitionMap, hiddenColumns]
    )

    const visibleHideableColumnsCount = visibleMiddleColumns.length

    const getColumnWidth = useCallback(
        (column: TAllKey) => {
            if (collapsedColumns.includes(column as TMiddleKey)) {
                return COLLAPSED_COLUMN_WIDTH
            }
            return columnWidths[column as string] ?? columnDefinitionMap[column]?.defaultWidth ?? 140
        },
        [collapsedColumns, columnDefinitionMap, columnWidths]
    )

    const isColumnCollapsed = useCallback((column: TAllKey) => collapsedColumns.includes(column as TMiddleKey), [collapsedColumns])

    const toggleCollapse = useCallback((column: TMiddleKey) => {
        userChangedLayout.current = true
        setCollapsedColumns((current) => (current.includes(column) ? current.filter((item) => item !== column) : [...current, column]))
    }, [])

    const hideColumn = useCallback(
        (column: TMiddleKey) => {
            if (visibleHideableColumnsCount <= 1) return
            userChangedLayout.current = true
            setHiddenColumns((current) => (current.includes(column) ? current : [...current, column]))
            setCollapsedColumns((current) => current.filter((item) => item !== column))
            if (activeColumn === column) setActiveColumn(null)
            if (overColumn === column) setOverColumn(null)
        },
        [activeColumn, overColumn, visibleHideableColumnsCount]
    )

    const addColumnToRight = useCallback((target: TMiddleKey, columnToAdd: TMiddleKey) => {
        userChangedLayout.current = true
        setHiddenColumns((current) => current.filter((item) => item !== columnToAdd))
        setMiddleColumnOrder((current) => {
            const nextOrder = current.filter((item) => item !== columnToAdd)
            const targetIndex = nextOrder.indexOf(target)
            if (targetIndex < 0) return [...nextOrder, columnToAdd]
            nextOrder.splice(targetIndex + 1, 0, columnToAdd)
            return nextOrder
        })
    }, [])

    const reorderColumns = useCallback((dragged: TMiddleKey, target: TMiddleKey, side: 'left' | 'right') => {
        if (dragged === target) return
        userChangedLayout.current = true
        setMiddleColumnOrder((current) => {
            const nextOrder = current.filter((col) => col !== dragged)
            const targetIndex = nextOrder.indexOf(target)
            if (targetIndex < 0) return current
            nextOrder.splice(side === 'left' ? targetIndex : targetIndex + 1, 0, dragged)
            return nextOrder
        })
    }, [])

    const resizeColumn = useCallback((column: TAllKey, width: number) => {
        userChangedLayout.current = true
        setColumnWidths((current) => ({ ...current, [column as string]: width }))
    }, [])

    const handleColumnsModalSave = useCallback((newOrder: TMiddleKey[], newHidden: TMiddleKey[]) => {
        userChangedLayout.current = true
        setMiddleColumnOrder(newOrder)
        setHiddenColumns(newHidden)
        setCollapsedColumns((current) => current.filter((col) => !newHidden.includes(col)))
    }, [])

    return {
        // State
        middleColumnOrder,
        hiddenColumns,
        collapsedColumns,
        columnWidths,
        activeColumn,
        overColumn,
        setActiveColumn,
        setOverColumn,
        // Derived
        columnDefinitionMap,
        visibleMiddleColumns,
        visibleColumns,
        addableColumns,
        visibleHideableColumnsCount,
        // Getters
        getColumnWidth,
        isColumnCollapsed,
        // Handlers
        toggleCollapse,
        hideColumn,
        addColumnToRight,
        reorderColumns,
        resizeColumn,
        handleColumnsModalSave,
    }
}
